import { useCallback, useEffect, useRef, useState } from 'react';
import { createChart, CrosshairMode, LineStyle } from 'lightweight-charts';
import type {
  IChartApi,
  ISeriesApi,
  CandlestickData,
  HistogramData,
  LineData,
  Time,
} from 'lightweight-charts';
import { computeMacd, computeStochastic } from '@/lib/indicators';
import { oklchChannelsToHex } from '@/lib/color';
import ChartToolbar, { type ChartTool } from '@/components/feature/ChartToolbar';
import ChartDrawingLayer, { type Drawing } from '@/components/feature/ChartDrawingLayer';

interface ChartTheme {
  bg: string;
  text: string;
  grid: string;
  border: string;
  macdLine: string;
  signal: string;
  stochK: string;
  stochD: string;
  trend: string;
  hline: string;
  font: string;
}

interface Props {
  data: CandlestickData<Time>[];
  symbol?: string;
  mainHeight?: number;
  subHeight?: number;
}

const UP_COLOR = '#26a69a';
const DOWN_COLOR = '#ef5350';

function cssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!raw) return fallback;
  return oklchChannelsToHex(raw) ?? fallback;
}

function getTheme(): ChartTheme {
  return {
    bg: cssVar('--background-100', '#1b1b20'),
    text: cssVar('--foreground-500', '#8b8b94'),
    grid: cssVar('--background-200', '#2a2a31'),
    border: cssVar('--background-300', '#3a3a42'),
    macdLine: cssVar('--accent-400', '#d1a05a'),
    signal: cssVar('--primary-400', '#e0c070'),
    stochK: cssVar('--accent-400', '#d1a05a'),
    stochD: cssVar('--primary-400', '#e0c070'),
    trend: cssVar('--primary-300', '#e6cf8f'),
    hline: cssVar('--accent-300', '#c9a97a'),
    font: typeof window === 'undefined' ? 'sans-serif' : getComputedStyle(document.body).fontFamily,
  };
}

/**
 * Dark three-pane chart: candles on top, MACD and Stochastic below.
 * Panes stay in sync and support hand-drawn trend lines + horizontal levels.
 */
export default function ProTradingChart({
  data,
  symbol,
  mainHeight = 320,
  subHeight = 104,
}: Props) {
  const mainRef = useRef<HTMLDivElement>(null);
  const macdRef = useRef<HTMLDivElement>(null);
  const stochRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const mainChartRef = useRef<IChartApi | null>(null);
  const candleRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const macdChartRef = useRef<IChartApi | null>(null);
  const macdHistRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const macdLineRef = useRef<ISeriesApi<'Line'> | null>(null);
  const macdSignalRef = useRef<ISeriesApi<'Line'> | null>(null);
  const stochChartRef = useRef<IChartApi | null>(null);
  const stochKRef = useRef<ISeriesApi<'Line'> | null>(null);
  const stochDRef = useRef<ISeriesApi<'Line'> | null>(null);

  const macdMapRef = useRef<Map<string, { macd: number | null }>>(new Map());
  const stochMapRef = useRef<Map<string, { k: number | null }>>(new Map());
  const themeRef = useRef<ChartTheme | null>(null);

  const fittedRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const seqRef = useRef(0);

  const [tool, setTool] = useState<ChartTool>('cursor');
  const [drawings, setDrawings] = useState<Drawing[]>([]);
  const [pending, setPending] = useState<{ time: Time; price: number } | null>(null);
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const [size, setSize] = useState({ w: 0, h: mainHeight });
  const [, setTick] = useState(0);

  const scheduleRedraw = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      setTick((t) => t + 1);
    });
  }, []);

  /* ---------- build the three stacked charts once ---------- */
  useEffect(() => {
    const mainEl = mainRef.current;
    const macdEl = macdRef.current;
    const stochEl = stochRef.current;
    if (!mainEl || !macdEl || !stochEl) return undefined;

    const th = getTheme();
    themeRef.current = th;

    const crosshair = {
      mode: CrosshairMode.Normal,
      vertLine: { color: th.border, width: 1 as const, style: LineStyle.Dashed, labelBackgroundColor: th.border },
      horzLine: { color: th.border, width: 1 as const, style: LineStyle.Dashed, labelBackgroundColor: th.border },
    };
    const grid = {
      vertLines: { color: th.grid, style: LineStyle.Dotted },
      horzLines: { color: th.grid, style: LineStyle.Dotted },
    };

    const main = createChart(mainEl, {
      width: mainEl.clientWidth,
      height: mainHeight,
      layout: { background: { type: 'solid', color: th.bg }, textColor: th.text, fontFamily: th.font, fontSize: 11 },
      grid,
      crosshair,
      rightPriceScale: { borderColor: th.border, minimumWidth: 72, scaleMargins: { top: 0.08, bottom: 0.08 } },
      timeScale: { borderColor: th.border, timeVisible: true, secondsVisible: false, visible: false, rightOffset: 3 },
      localization: { locale: 'vi-VN' },
      handleScroll: { vertTouchDrag: false },
    });

    const candles = main.addCandlestickSeries({
      upColor: UP_COLOR,
      downColor: DOWN_COLOR,
      borderUpColor: UP_COLOR,
      borderDownColor: DOWN_COLOR,
      wickUpColor: UP_COLOR,
      wickDownColor: DOWN_COLOR,
      priceLineVisible: true,
      lastValueVisible: true,
    });

    const buildPane = (el: HTMLElement, showTime: boolean) =>
      createChart(el, {
        width: mainEl.clientWidth,
        height: subHeight,
        layout: {
          background: { type: 'solid' as const, color: th.bg },
          textColor: th.text,
          fontFamily: th.font,
          fontSize: 10,
        },
        grid,
        crosshair,
        rightPriceScale: { borderColor: th.border, minimumWidth: 72 },
        timeScale: { borderColor: th.border, timeVisible: showTime, secondsVisible: false, visible: showTime },
        localization: { locale: 'vi-VN' },
        handleScroll: false,
        handleScale: false,
      });

    const macdChart = buildPane(macdEl, false);
    const histSeries = macdChart.addHistogramSeries({
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
      priceLineVisible: false,
      lastValueVisible: false,
    });
    const macdLineSeries = macdChart.addLineSeries({
      color: th.macdLine,
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });
    const signalSeries = macdChart.addLineSeries({
      color: th.signal,
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });

    const stochChart = buildPane(stochEl, true);
    const kSeries = stochChart.addLineSeries({
      color: th.stochK,
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });
    const dSeries = stochChart.addLineSeries({
      color: th.stochD,
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });
    kSeries.createPriceLine({ price: 80, color: th.grid, lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: false, title: '' });
    kSeries.createPriceLine({ price: 20, color: th.grid, lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: false, title: '' });

    mainChartRef.current = main;
    candleRef.current = candles;
    macdChartRef.current = macdChart;
    macdHistRef.current = histSeries;
    macdLineRef.current = macdLineSeries;
    macdSignalRef.current = signalSeries;
    stochChartRef.current = stochChart;
    stochKRef.current = kSeries;
    stochDRef.current = dSeries;

    const syncRange = () => {
      const range = main.timeScale().getVisibleLogicalRange();
      if (range) {
        macdChart.timeScale().setVisibleLogicalRange(range);
        stochChart.timeScale().setVisibleLogicalRange(range);
      }
      scheduleRedraw();
    };
    main.timeScale().subscribeVisibleLogicalRangeChange(syncRange);

    const onCrosshair = (param: { time?: Time }) => {
      const time = param?.time;
      if (!time) {
        macdChart.clearCrosshairPosition();
        stochChart.clearCrosshairPosition();
        return;
      }
      const key = String(time);
      const macdValue = macdMapRef.current.get(key)?.macd;
      const stochValue = stochMapRef.current.get(key)?.k;
      if (macdValue != null && macdLineRef.current) {
        macdChart.setCrosshairPosition(macdValue, time, macdLineRef.current);
      } else {
        macdChart.clearCrosshairPosition();
      }
      if (stochValue != null && stochKRef.current) {
        stochChart.setCrosshairPosition(stochValue, time, stochKRef.current);
      } else {
        stochChart.clearCrosshairPosition();
      }
    };
    main.subscribeCrosshairMove(onCrosshair);

    const applyWidths = () => {
      const w = mainEl.clientWidth;
      main.applyOptions({ width: w });
      macdChart.applyOptions({ width: w });
      stochChart.applyOptions({ width: w });
      setSize({ w, h: mainHeight });
    };
    applyWidths();

    const ro = new ResizeObserver(() => {
      applyWidths();
      scheduleRedraw();
    });
    ro.observe(mainEl);
    scheduleRedraw();

    return () => {
      ro.disconnect();
      main.timeScale().unsubscribeVisibleLogicalRangeChange(syncRange);
      main.unsubscribeCrosshairMove(onCrosshair);
      main.remove();
      macdChart.remove();
      stochChart.remove();
      mainChartRef.current = null;
      candleRef.current = null;
      macdChartRef.current = null;
      macdHistRef.current = null;
      macdLineRef.current = null;
      macdSignalRef.current = null;
      stochChartRef.current = null;
      stochKRef.current = null;
      stochDRef.current = null;
      fittedRef.current = false;
    };
  }, [mainHeight, subHeight, scheduleRedraw]);

  /* ---------- push candle data + indicator series ---------- */
  useEffect(() => {
    const candles = candleRef.current;
    const histSeries = macdHistRef.current;
    const macdLineSeries = macdLineRef.current;
    const signalSeries = macdSignalRef.current;
    const kSeries = stochKRef.current;
    const dSeries = stochDRef.current;
    if (!candles || !histSeries || !macdLineSeries || !signalSeries || !kSeries || !dSeries) return;
    if (data.length === 0) return;

    candles.setData(data);

    const macdMap = new Map<string, { macd: number | null }>();
    const histData: HistogramData<Time>[] = [];
    const macdLineData: LineData<Time>[] = [];
    const signalData: LineData<Time>[] = [];
    computeMacd(data).forEach((point) => {
      macdMap.set(String(point.time), { macd: point.macd });
      if (point.hist != null) {
        histData.push({
          time: point.time,
          value: point.hist,
          color: point.hist >= 0 ? `${UP_COLOR}99` : `${DOWN_COLOR}99`,
        });
      }
      if (point.macd != null) macdLineData.push({ time: point.time, value: point.macd });
      if (point.signal != null) signalData.push({ time: point.time, value: point.signal });
    });
    macdMapRef.current = macdMap;
    histSeries.setData(histData);
    macdLineSeries.setData(macdLineData);
    signalSeries.setData(signalData);

    const stochMap = new Map<string, { k: number | null }>();
    const kData: LineData<Time>[] = [];
    const dData: LineData<Time>[] = [];
    computeStochastic(data).forEach((point) => {
      stochMap.set(String(point.time), { k: point.k });
      if (point.k != null) kData.push({ time: point.time, value: point.k });
      if (point.d != null) dData.push({ time: point.time, value: point.d });
    });
    stochMapRef.current = stochMap;
    kSeries.setData(kData);
    dSeries.setData(dData);

    if (!fittedRef.current && mainChartRef.current) {
      mainChartRef.current.timeScale().fitContent();
      const range = mainChartRef.current.timeScale().getVisibleLogicalRange();
      if (range) {
        macdChartRef.current?.timeScale().setVisibleLogicalRange(range);
        stochChartRef.current?.timeScale().setVisibleLogicalRange(range);
      }
      fittedRef.current = true;
    }

    scheduleRedraw();
  }, [data, scheduleRedraw]);

  /* ---------- cancel an in-progress drawing with Escape ---------- */
  useEffect(() => {
    if (!pending) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPending(null);
        setHover(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending]);

  const toChartPoint = (event: React.MouseEvent) => {
    const svg = svgRef.current;
    const chart = mainChartRef.current;
    const series = candleRef.current;
    if (!svg || !chart || !series) return null;
    const rect = svg.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const time = chart.timeScale().coordinateToTime(x) as Time | null;
    const price = series.coordinateToPrice(y) as number | null;
    if (time == null || price == null) return null;
    return { time, price };
  };

  const handleOverlayClick = (event: React.MouseEvent) => {
    const point = toChartPoint(event);
    if (!point) return;
    seqRef.current += 1;

    if (tool === 'hline') {
      setDrawings((prev) => [...prev, { id: `d${seqRef.current}`, type: 'hline', price: point.price }]);
      return;
    }

    if (tool === 'trend') {
      if (!pending) {
        setPending({ time: point.time, price: point.price });
      } else {
        setDrawings((prev) => [
          ...prev,
          {
            id: `d${seqRef.current}`,
            type: 'trend',
            t1: pending.time,
            p1: pending.price,
            t2: point.time,
            p2: point.price,
          },
        ]);
        setPending(null);
        setHover(null);
      }
    }
  };

  const handleOverlayMove = (event: React.MouseEvent) => {
    if (tool !== 'trend' || !pending) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    setHover({ x: event.clientX - rect.left, y: event.clientY - rect.top });
  };

  const clearAll = () => {
    setDrawings([]);
    setPending(null);
    setHover(null);
  };

  const changeTool = (next: ChartTool) => {
    setTool(next);
    setPending(null);
    setHover(null);
  };

  const chart = mainChartRef.current;
  const series = candleRef.current;
  const th = themeRef.current;

  const toX = (time: Time): number | null => {
    if (!chart) return null;
    const coord = chart.timeScale().timeToCoordinate(time);
    return coord == null ? null : (coord as number);
  };
  const toY = (price: number): number | null => {
    if (!series) return null;
    const coord = series.priceToCoordinate(price);
    return coord == null ? null : (coord as number);
  };

  const interactive = tool !== 'cursor';
  const hint =
    tool === 'trend'
      ? pending
        ? 'Bấm điểm thứ hai để hoàn tất đường xu hướng.'
        : 'Bấm điểm thứ nhất trên biểu đồ để bắt đầu vẽ đường xu hướng.'
      : 'Bấm vào biểu đồ để đặt đường ngang hỗ trợ / kháng cự.';

  return (
    <div>
      <div className="mb-2">
        <ChartToolbar
          tool={tool}
          onToolChange={changeTool}
          drawingCount={drawings.length}
          onClear={clearAll}
        />
      </div>

      {interactive && <p className="mb-2 text-[11px] text-foreground-500">{hint}</p>}

      {/* Main price pane */}
      <div className="relative" style={{ height: mainHeight }}>
        <div ref={mainRef} style={{ height: mainHeight }} />

        <div className="absolute left-2 top-2 z-20 pointer-events-none flex items-center gap-2 text-[11px]">
          <span className="font-semibold text-foreground-800">{symbol || 'XAU/VND'}</span>
          <span className="text-foreground-500">· Khung 15 phút</span>
        </div>

        <svg
          ref={svgRef}
          className="absolute left-0 top-0 z-10"
          width={size.w}
          height={size.h}
          style={{
            pointerEvents: interactive ? 'auto' : 'none',
            cursor: interactive ? 'crosshair' : 'default',
          }}
          onClick={handleOverlayClick}
          onMouseMove={handleOverlayMove}
          onMouseLeave={() => setHover(null)}
        >
          <ChartDrawingLayer
            drawings={drawings}
            pending={pending}
            hover={hover}
            width={size.w}
            toX={toX}
            toY={toY}
            trendColor={th?.trend}
            hlineColor={th?.hline}
            labelTextColor={th?.bg}
          />
        </svg>
      </div>

      {/* MACD pane */}
      <div className="relative mt-1" style={{ height: subHeight }}>
        <div ref={macdRef} style={{ height: subHeight }} />
        <span className="absolute left-2 top-1.5 z-20 pointer-events-none text-[10px] text-foreground-500">
          MACD (12, 26, 9)
        </span>
      </div>

      {/* Stochastic pane */}
      <div className="relative mt-1" style={{ height: subHeight }}>
        <div ref={stochRef} style={{ height: subHeight }} />
        <span className="absolute left-2 top-1.5 z-20 pointer-events-none text-[10px] text-foreground-500">
          Stochastic (9, 3, 3)
        </span>
      </div>
    </div>
  );
}